import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead } from "@/components/shared";

const PACKAGES = [
  {
    name: "Island Essentials",
    price: "$500",
    per: "per month",
    features: ["8 posts per month", "Monthly content calendar", "Caption copywriting", "Hashtag & search basics", "Monthly performance snapshot"],
    featured: false,
  },
  {
    name: "Signature Growth",
    price: "$950",
    per: "per month",
    features: ["12 posts per month", "Stories & highlights", "Comments & message replies", "Guest content curation", "Monthly report + strategy call"],
    featured: true,
  },
  {
    name: "Concierge Social",
    price: "$1,500+",
    per: "per month",
    features: ["16 posts per month", "Reels direction & editing guidance", "Full engagement management", "Seasonal campaigns & offers", "Direct-booking funnel to your website"],
    featured: false,
  },
];

export default function SocialPackages() {
  return (
    <section className="mt-24" data-testid="social-packages">
      <SectionHead
        overline="Monthly Packages"
        title="Social media, handled monthly."
        copy="Guide pricing — every package is tailored after a short conversation about your property and goals."
      />
      <div className="mt-12 grid gap-8 lg:grid-cols-3">
        {PACKAGES.map((p, i) => (
          <Reveal key={p.name} delay={i * 0.08} className="h-full">
            <div
              data-testid={`social-package-${p.name.toLowerCase().replace(/\s/g, "-")}`}
              className={`flex h-full flex-col p-8 lg:p-9 ${p.featured ? "bg-navy text-ivory shadow-2xl shadow-navy/20" : "border border-navy/10 bg-white"}`}
            >
              <h3 className={`font-serif text-2xl ${p.featured ? "text-ivory" : "text-navy"}`}>{p.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className={`font-serif text-5xl font-light ${p.featured ? "text-bermuda" : "text-navy"}`}>{p.price}</span>
                <span className={`text-xs ${p.featured ? "text-ivory/60" : "text-navy/50"}`}>{p.per}</span>
              </div>
              <ul className="mt-7 flex-1 space-y-3">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[13px]">
                    <Check size={14} strokeWidth={2.5} className={`mt-0.5 shrink-0 ${p.featured ? "text-bermuda" : "text-ocean"}`} />
                    <span className={p.featured ? "text-ivory/85" : "text-navy/75"}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/consultation"
                data-testid={`social-package-cta-${i}`}
                className={`mt-8 block px-6 py-3.5 text-center text-[11px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  p.featured ? "bg-bermuda text-navy hover:bg-ivory" : "border border-navy/25 text-navy hover:bg-navy hover:text-ivory"
                }`}
              >
                Book a Consultation
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-10 grid gap-px bg-navy/10 sm:grid-cols-2" data-testid="social-pricing-notes">
          <div className="bg-sand-light p-7">
            <h4 className="font-serif text-lg text-navy">Hourly support — from $75/hour</h4>
            <p className="mt-2 text-[13px] leading-relaxed text-navy/60">
              For audits, one-off content sessions and ad-hoc help. Rates vary with complexity — strategy and
              content production sit above simple scheduling.
            </p>
          </div>
          <div className="bg-sand-light p-7">
            <h4 className="font-serif text-lg text-navy">Project-based pricing</h4>
            <p className="mt-2 text-[13px] leading-relaxed text-navy/60">
              Campaigns, launches and audience-growth initiatives are quoted per project — scoped in writing
              before any work begins.
            </p>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
